import React from 'react';
import { PassData } from './types';

// "How it works" — 3 numbered steps with small phone illustrations, per the
// desktop target design. Phones are pure SVG/CSS (no image assets → no CSP
// changes); the mini pass inside each phone picks up the merchant's card
// colour so the illustration matches the pass above it.

const MiniPhone: React.FC<{ variant: 'tap' | 'wallet' | 'use'; cardColor: string }> = ({ variant, cardColor }) => (
  <div className="mx-auto w-[92px] h-[176px] rounded-[18px] border-[3px] border-gray-900 bg-white shadow-sm overflow-hidden relative">
    {/* notch */}
    <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-1.5 rounded-full bg-gray-900" />
    <div className="pt-4 px-2 h-full">
      {variant === 'tap' && (
        <>
          <div className="rounded-md h-16 mb-2 p-1.5" style={{ backgroundColor: cardColor }}>
            <div className="w-6 h-1.5 rounded bg-white/60 mb-1" />
            <div className="w-10 h-1.5 rounded bg-white/40 mb-2" />
            <div className="mx-auto w-12 h-4 rounded-sm bg-white flex items-center justify-center">
              <div className="w-9 h-2 bg-[repeating-linear-gradient(90deg,#000,#000_1px,#fff_1px,#fff_2px)]" />
            </div>
          </div>
          <div className="rounded-full h-5 bg-gray-900 mb-1.5 flex items-center justify-center">
            <span className="text-[5px] text-white font-medium">Add to Google Wallet</span>
          </div>
          <div className="rounded-full h-5 bg-gray-900 flex items-center justify-center relative">
            <span className="text-[5px] text-white font-medium">Add to Apple Wallet</span>
            {/* tap cursor */}
            <svg className="absolute -right-1.5 -bottom-2 w-4 h-4" viewBox="0 0 24 24" fill="#fff" stroke="#111" strokeWidth="1.5" aria-hidden>
              <path d="M6 3l4 15 2.5-5.5L18 10z" />
            </svg>
          </div>
        </>
      )}
      {variant === 'wallet' && (
        <>
          <p className="text-[6px] font-semibold text-gray-900 mb-1">Wallet</p>
          <div className="rounded-md h-20 p-1.5" style={{ backgroundColor: cardColor }}>
            <div className="w-6 h-1.5 rounded bg-white/60 mb-1" />
            <div className="w-10 h-1.5 rounded bg-white/40 mb-2.5" />
            <div className="flex gap-1 mb-2">
              <div className="w-5 h-1 rounded bg-white/40" />
              <div className="w-5 h-1 rounded bg-white/40" />
            </div>
            <div className="mx-auto w-14 h-5 rounded-sm bg-white flex items-center justify-center">
              <div className="w-11 h-3 bg-[repeating-linear-gradient(90deg,#000,#000_1px,#fff_1px,#fff_2px)]" />
            </div>
          </div>
          <div className="mt-1.5 rounded-md h-6 bg-gray-100" />
          <div className="mt-1 rounded-md h-6 bg-gray-100" />
        </>
      )}
      {variant === 'use' && (
        <>
          <div className="rounded-md h-14 p-1.5 mb-2" style={{ backgroundColor: cardColor }}>
            <div className="w-6 h-1.5 rounded bg-white/60 mb-1" />
            <div className="mx-auto mt-2 w-12 h-4 rounded-sm bg-white flex items-center justify-center">
              <div className="w-9 h-2 bg-[repeating-linear-gradient(90deg,#000,#000_1px,#fff_1px,#fff_2px)]" />
            </div>
          </div>
          <div className="mx-auto w-9 h-9 rounded-full bg-[#AEDD1F] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#06251B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[6px] text-center font-semibold text-gray-900 mt-1">Ready to use</p>
        </>
      )}
    </div>
  </div>
);

const STEPS = [
  {
    title: 'Tap “Add”',
    body: 'Tap one of the buttons to add this pass to your wallet.',
    variant: 'tap' as const,
  },
  {
    title: 'View in your wallet',
    body: 'Your pass will be added to your wallet instantly.',
    variant: 'wallet' as const,
  },
  {
    title: 'Use & enjoy',
    body: 'Show your pass in-store to earn points and unlock rewards.',
    variant: 'use' as const,
  },
];

const HowItWorks: React.FC<{ passData: PassData; showIllustrations?: boolean }> = ({
  passData,
  showIllustrations = true,
}) => {
  const cardColor = passData.background_colour || '#1c1c1e';
  return (
    <div>
      <p className="text-sm text-gray-500 mb-5">Add your pass in 3 simple steps.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {STEPS.map((step, i) => (
          <div key={step.title}>
            <div className="flex items-center gap-2.5 mb-1.5">
              <span className="w-6 h-6 rounded-full bg-[#AEDD1F] text-[#06251B] text-xs font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">{step.body}</p>
            {showIllustrations && <MiniPhone variant={step.variant} cardColor={cardColor} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HowItWorks;
